/**
 * File: MobileNav.tsx
 * Path: apps/admin/src/modules/layout/components/MobileNav.tsx
 * Description: Mobile navigation with hardened column-grid distribution and complete icon mapping.
 */

import React from "react";
import { Link, NavLink, To } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { LogOut, Palette, X } from "lucide-react";
import { Button } from "@idk/ui";
import { ADMIN_PATHS } from "@idk/config";
import { useMobileNavController } from "@/modules/layout/hooks/useMobileNavController";
import { groupIsActive } from "@/modules/layout/utils/navActiveUtils";
import { UnreadBadge, UnreadDot } from "@/modules/layout/components/UnreadBadge";
import { navIcon } from "../utils/navIconsUtils";

const luxuryEase = [0.16, 1, 0.3, 1] as const;

export const MobileNav: React.FC = () => {
  const { pathname, isMoreOpen, dockItems, drawerItems, isPending, closeMenu, toggleMore, signOut } =
    useMobileNavController();

  const dockLinkClass = ({ isActive }: { isActive: boolean }) =>
    `flex flex-col items-center justify-center w-full h-full space-y-stack-xs transition-colors rounded-none ${
      isActive ? "text-primary" : "text-muted-foreground hover:text-foreground"
    }`;

  const menuLinkClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center w-full px-gutter-md py-stack-md rounded-none transition-colors ${
      isActive
        ? "bg-foreground/5 text-primary border-l-2 border-primary"
        : "text-foreground border-l-2 border-transparent hover:bg-foreground/5"
    }`;

  return (
    <>
      {/* 1. BOTTOM DOCK (FIXED: Converted from Flex to uniform 5-Column Grid distribution) */}
      <nav
        aria-label="Main"
        className="sm:hidden fixed bottom-0 left-0 right-0 h-20 bg-background border-t border-border/60 z-50 grid px-gutter-xs pb-safe"
        style={{ gridTemplateColumns: `repeat(${dockItems.length + 1}, minmax(0, 1fr))` }}
      >
        {dockItems.map((item) => {
          const Icon = navIcon(item.icon);
          const to = (item.hub ?? item.href) as To;
          const label = item.href === ADMIN_PATHS.dashboard ? "Home" : item.label;
          const isActive = item.hub ? groupIsActive(item, pathname) : pathname === item.href;
          return (
            <Link
              key={item.label}
              to={to}
              onClick={closeMenu}
              aria-current={isActive ? "page" : undefined}
              className={dockLinkClass({ isActive })}
            >
              <Icon className="h-[22px] w-[22px]" strokeWidth={1.5} />
              <span className="text-xs font-semibold text-center truncate w-full">{label}</span>
            </Link>
          );
        })}

        <button
          onClick={toggleMore}
          className={`flex flex-col items-center justify-center w-full h-full space-y-stack-xs transition-colors rounded-none ${
            isMoreOpen ? "text-primary" : "text-muted-foreground"
          }`}
        >
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          >
            <line x1="3" y1="6" x2="21" y2="6" />
            <motion.line
              x1="3"
              y1="12"
              x2="21"
              y2="12"
              animate={{ scaleX: isMoreOpen ? 0.5 : 1 }}
              style={{ originX: 1 }}
              transition={{ duration: 0.4, ease: luxuryEase }}
            />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
          <span className="relative text-xs font-semibold">
            More
            <UnreadDot className="absolute -right-stack-sm -top-stack-xs" />
          </span>
        </button>
      </nav>

      {/* 2. MORE DRAWER */}
      <AnimatePresence>
        {isMoreOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeMenu}
              className="sm:hidden fixed inset-0 bg-background/60 backdrop-blur-sm z-40"
            />

            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="sm:hidden fixed bottom-20 left-0 right-0 bg-background border-t border-border/60 shadow-2xl z-40 overflow-hidden flex flex-col rounded-none"
            >
              <div className="flex items-center justify-between px-gutter-sm py-stack-sm border-b border-border/40">
                <span className="text-xs font-black uppercase tracking-wider text-muted-foreground">
                  More
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={closeMenu}
                  className="rounded-none"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>

              <div className="flex flex-col p-gutter-xs space-y-stack-xs max-h-[70vh] overflow-y-auto no-scrollbar">
                {drawerItems.map((item) => (
                  <React.Fragment key={item.label}>
                    {item.children && item.children.length > 0 ? (
                      <div className="flex flex-col w-full">
                        <div className="px-gutter-md py-stack-xs">
                          <p className="text-xs font-black uppercase tracking-wider text-muted-foreground">
                            {item.label}
                          </p>
                        </div>
                        {item.children.map((child) => {
                          const ChildIcon = navIcon(child.icon);
                          if (!child.href) return null;
                          return (
                            <NavLink
                              key={child.href}
                              to={child.href as To}
                              onClick={closeMenu}
                              className={menuLinkClass}
                            >
                              <ChildIcon
                                className="h-[18px] w-[18px] mr-stack-md"
                                strokeWidth={1.5}
                              />
                              <span className="text-xs font-bold uppercase tracking-wider">
                                {child.label}
                              </span>
                              {child.href === ADMIN_PATHS.notifications && (
                                <UnreadBadge className="ml-auto" />
                              )}
                            </NavLink>
                          );
                        })}
                      </div>
                    ) : (
                      item.href && (
                        <NavLink
                          to={item.href as To}
                          onClick={closeMenu}
                          className={menuLinkClass}
                        >
                          {React.createElement(navIcon(item.icon), {
                            className: "h-[18px] w-[18px] mr-stack-md",
                            strokeWidth: 1.5,
                          })}
                          <span className="text-xs font-bold uppercase tracking-wider">
                            {item.label}
                          </span>
                        </NavLink>
                      )
                    )}
                  </React.Fragment>
                ))}

                <div className="h-px w-full bg-border/40 my-stack-xs" />

                <NavLink
                  to={`${ADMIN_PATHS.settings}?tab=appearance`}
                  onClick={closeMenu}
                  className={menuLinkClass({ isActive: false })}
                >
                  <Palette className="h-[18px] w-[18px] mr-stack-md" strokeWidth={1.5} />
                  <span className="text-xs font-bold uppercase tracking-wider">Appearance</span>
                </NavLink>

                <button
                  onClick={signOut}
                  disabled={isPending}
                  className="flex items-center w-full px-gutter-md py-stack-md rounded-none text-destructive hover:bg-destructive/10 transition-colors border-none text-left"
                >
                  <LogOut
                    className="h-[18px] w-[18px] mr-stack-md"
                    strokeWidth={1.5}
                  />
                  <span className="text-xs font-bold uppercase tracking-wider">
                    {isPending ? "Signing out..." : "Sign out"}
                  </span>
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};